import { toCurrencyPrecisionCeil } from '@openeducation/pp-js-utils';
import type {
  Currency,
  ExtendedPaymentInfoFormData,
} from '@openeducation/pp-rn-shared-types';
import { PaymentMethod } from '@openeducation/pp-rn-shared-types';
import { useCallback } from 'react';

interface UseTwoCreditCardAmountsProps {
  formData: ExtendedPaymentInfoFormData;
  totalAmount: number;
  currency?: Currency;
  onFieldChange: (value: string, fieldName: string) => void;
}

export const useTwoCreditCardAmounts = ({
  formData,
  totalAmount,
  currency,
  onFieldChange,
}: UseTwoCreditCardAmountsProps) => {
  const isTwoCreditCards =
    formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS;

  const handleCard1AmountChange = useCallback(
    (value: string) => {
      onFieldChange(value, 'card1_amount');
      if (isTwoCreditCards && totalAmount > 0) {
        const firstAmount = parseFloat(value) || 0;
        const remaining = totalAmount - firstAmount;

        onFieldChange(
          remaining > 0
            ? toCurrencyPrecisionCeil(remaining, currency)
            : toCurrencyPrecisionCeil(0, currency),
          'card2_amount'
        );
      }
    },
    [onFieldChange, isTwoCreditCards, totalAmount, currency]
  );

  const handleCard1AmountBlur = useCallback(
    (value: string) => {
      const amount = parseFloat(value) || 0;

      handleCard1AmountChange(toCurrencyPrecisionCeil(amount, currency));
    },
    [handleCard1AmountChange]
  );

  return {
    isTwoCreditCards,
    handleCard1AmountChange,
    handleCard1AmountBlur,
  };
};
